import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ColorLegend from '../components/ColorLegend';
import OutreachStageDot, { stageTextColor } from '../components/OutreachStageDot.jsx';

// Inactive Sites page. One row per site where status='Inactive',
// across every customer. Useful as a "graveyard / churn" view -
// what's been retired and who used to own it.
//
// To take a site OUT of this view: open the parent customer, find
// the site in the Sites tab, click Edit Site, flip the Status
// dropdown back to Active.

// Highlight palette - duplicate of Customers.jsx so a future refactor
// could pull this into a shared module. Same hex values; tracking the
// same orange/green precedence used by effectiveColor on Customers.
const HIGHLIGHT_PALETTE = {
  orange: '#ED7D31',
  red:    '#b91c1c',
  amber:  '#b45309',
  yellow: '#ca8a04',
  green:  '#047857',
  blue:   '#1e40af',
  purple: '#5b21b6',
  grey:   '#6b7280',
  // Outreach cadence colors (Dave 2026-06-09). Different keys from the
  // historical highlight palette so the legend chips can disambiguate
  // "I sent a follow-up to this lead" vs "I bumped this customer's
  // price increase". Same readable text shades as OutreachStageDot's
  // stageTextColor() so the legend matches the body text.
  cadenceYellow: '#a16207',
  cadenceOrange: '#c2410c',
  cadenceRed:    '#b91c1c'
};

// Legend chip labels. Default to the color name; override for the
// outreach-cadence chips so users see what the color actually means
// instead of trying to interpret "cadenceYellow".
const LEGEND_LABEL = {
  cadenceYellow: 'Initial sent',
  cadenceOrange: 'Follow-up sent',
  cadenceRed:    'Breakup sent',
  green:         'Reactivation sent',
  orange:        'Price-increase sent'
};

// Resolve the effective color name for an inactive site row:
//   outreach_stage (yellow/orange/red) > manual highlight > price-increase orange > reactivation green
// Returns null when none apply. Used both for the colored render on
// the company name and for the color-filter chips.
//
// Outreach stage wins (Dave 2026-06-09): the cadence color is the most
// actionable signal for Zoe/Kaden picking work. The yellow/orange/red
// here uses different names than the historical highlight palette
// ('cadenceYellow' / 'cadenceOrange' / 'cadenceRed') so chip selection
// and effectiveColor stay unambiguous when, for example, "orange"
// could mean either price-increase OR cadence followup.
function effectiveColor(s) {
  if (s.outreach_stage === 'initial')  return 'cadenceYellow';
  if (s.outreach_stage === 'followup') return 'cadenceOrange';
  if (s.outreach_stage === 'breakup')  return 'cadenceRed';
  if (s.highlight_color)        return s.highlight_color;
  if (s.price_increase_sent)    return 'orange';
  if (s.reactivation_sent)      return 'green';
  return null;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function InactiveSites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('company');
  const [sortDir, setSortDir] = useState('asc');
  // Color filter (null = all colors). When set, list scopes to rows
  // whose effective color matches. Mirrors the Customers page filter.
  const [colorFilter, setColorFilter] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/sites/inactive')
      .then((r) => { if (!cancelled) setSites(r.data?.sites || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Counts per effective color across ALL inactive sites (not just the
  // currently-filtered view) so chip counts are stable when typing in
  // the search box.
  //
  // Dave 2026-06-09: on InactiveSites we only want the OUTREACH cadence
  // chips (Initial / Follow-up / Breakup) -- the older reactivation,
  // price-increase, and manual-highlight chips were noise that
  // distracted from the cadence work. effectiveColor() still computes
  // historical colors so the company name stays green for reactivated
  // customers / orange for price-increase, but the chip strip + legend
  // filter to cadence only.
  const colorCounts = useMemo(() => {
    const counts = {};
    for (const s of sites) {
      const name = effectiveColor(s);
      if (!name) continue;
      if (!name.startsWith('cadence')) continue;
      counts[name] = (counts[name] || 0) + 1;
    }
    return counts;
  }, [sites]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = sites;
    if (colorFilter) {
      out = out.filter((s) => effectiveColor(s) === colorFilter);
    }
    if (q) {
      out = out.filter((s) => {
        const hay = [
          s.company, s.first_name, s.last_name,
          s.site_number, s.site_name, s.city, s.province_state,
          s.fuel_brand, s.pos_type, s.owner_name
        ].map((v) => v == null ? '' : String(v).toLowerCase()).join(' | ');
        return hay.includes(q);
      });
    }
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...out].sort((a, b) => {
      const av = a[sortBy] ?? '';
      const bv = b[sortBy] ?? '';
      const aE = av === '' || av == null;
      const bE = bv === '' || bv == null;
      if (aE && bE) return 0;
      if (aE) return 1;
      if (bE) return -1;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * sign;
      if (as > bs) return  1 * sign;
      return 0;
    });
  }, [sites, query, sortBy, sortDir, colorFilter]);

  function onHeader(key) {
    if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(key); setSortDir('asc'); }
  }
  function arrow(key) {
    if (sortBy !== key) return '';
    return sortDir === 'desc' ? ' ▾' : ' ▴';
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Inactive Sites</h2>
          <div className="subtitle">
            Sites with status='Inactive'. Edit the site on its customer page and flip Status back to Active to remove it from this list.
          </div>
        </div>
      </div>

      {/* Color filter chips — one per effective color present in the
          list. Click to scope, click active chip (or Clear) to reset.
          Mirrors the Customers page filter so the two views feel
          consistent. The most common chip here is Green ('reactivation
          email sent') — that's the signal Dave needs to spot which
          inactive customers have already been touched in the win-back
          campaign. */}
      {Object.keys(colorCounts).length > 0 && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 'var(--space-3)'
        }}>
          <span className="result-count" style={{ marginRight: 'var(--space-1)' }}>
            Filter by color:
          </span>
          {Object.entries(colorCounts).sort((a, b) => b[1] - a[1]).map(([name, count]) => {
            const active = colorFilter === name;
            const hex = HIGHLIGHT_PALETTE[name];
            return (
              <button
                key={name}
                type="button"
                onClick={() => setColorFilter(active ? null : name)}
                style={{
                  padding: '4px 10px',
                  background: active ? hex : 'var(--color-surface)',
                  color:      active ? '#fff' : hex,
                  border:     `1px solid ${hex}`,
                  borderRadius: 'var(--radius-pill, 999px)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-semibold)',
                  fontSize: 'var(--text-small)',
                  textTransform: 'capitalize'
                }}
                title={active ? 'Click again to clear' : `Show only ${LEGEND_LABEL[name] || name}-highlighted inactive sites`}
              >
                {LEGEND_LABEL[name] || name} <span style={{ marginLeft: 4, fontFamily: 'var(--font-mono)' }}>{count}</span>
              </button>
            );
          })}
          {colorFilter && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setColorFilter(null)}
              style={{ marginLeft: 'var(--space-1)' }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      <ColorLegend colorCounts={colorCounts} />

      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        marginBottom: 'var(--space-3)'
      }}>
        <input
          type="search"
          placeholder="Search company, site #, city, fuel brand…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ minWidth: 280, flex: '0 0 auto' }}
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${visible.length} of ${sites.length} inactive sites`}
        </span>
      </div>

      {error && <p className="error">{error}</p>}
      {!loading && !error && sites.length === 0 && (
        <p className="panel-empty">No inactive sites. Everything's active.</p>
      )}

      {!loading && !error && sites.length > 0 && (
        <div className="panel" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th onClick={() => onHeader('company')}        style={{ cursor: 'pointer', textAlign: 'left' }}>Customer{arrow('company')}</th>
                <th onClick={() => onHeader('site_name')}      style={{ cursor: 'pointer', textAlign: 'left' }}>Site Name{arrow('site_name')}</th>
                <th onClick={() => onHeader('site_number')}    style={{ cursor: 'pointer', textAlign: 'left' }}>Site #{arrow('site_number')}</th>
                <th onClick={() => onHeader('city')}           style={{ cursor: 'pointer', textAlign: 'left' }}>City{arrow('city')}</th>
                <th onClick={() => onHeader('province_state')} style={{ cursor: 'pointer', textAlign: 'left' }}>Prov / State{arrow('province_state')}</th>
                <th onClick={() => onHeader('owner_name')}     style={{ cursor: 'pointer', textAlign: 'left' }}>Owner{arrow('owner_name')}</th>
                <th onClick={() => onHeader('fuel_brand')}     style={{ cursor: 'pointer', textAlign: 'left' }}>Fuel Brand{arrow('fuel_brand')}</th>
                <th onClick={() => onHeader('pos_type')}       style={{ cursor: 'pointer', textAlign: 'left' }}>POS Type{arrow('pos_type')}</th>
                <th onClick={() => onHeader('install_date')}   style={{ cursor: 'pointer', textAlign: 'left' }}>Installed{arrow('install_date')}</th>
                <th onClick={() => onHeader('renewal_date')}   style={{ cursor: 'pointer', textAlign: 'left' }}>Renewal{arrow('renewal_date')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => navigate(`/customers/${s.customer_id}`)}
                  style={{ cursor: 'pointer' }}
                  title={`Open ${s.company || 'customer'}`}
                >
                  <td>
                    {/* Color precedence (Dave 2026-06-09):
                        outreach_stage (yellow/orange/red) > reactivation green.
                        The cadence color is the most actionable signal for
                        Zoe/Kaden; the win-back green is historical. */}
                    {(() => {
                      const stageColor = stageTextColor(s.outreach_stage);
                      const color = stageColor || (s.reactivation_sent ? '#047857' : undefined);
                      const tip = s.outreach_stage
                        ? 'Outreach cadence in progress'
                        : (s.reactivation_sent ? 'Reactivation email has been sent' : undefined);
                      return (
                        <div
                          style={{ fontWeight: 'var(--font-semibold)', color, display: 'flex', alignItems: 'center' }}
                          title={tip}
                        >
                          <OutreachStageDot stage={s.outreach_stage} />
                          {s.company || '—'}
                        </div>
                      );
                    })()}
                    {(s.first_name || s.last_name) && (
                      <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                        {[s.first_name, s.last_name].filter(Boolean).join(' ')}
                      </div>
                    )}
                  </td>
                  <td>{s.site_name || '—'}</td>
                  <td className="cell-mono">{s.site_number || '—'}</td>
                  <td>{s.city || '—'}</td>
                  <td>{s.province_state || '—'}</td>
                  <td>
                    {s.owner_name
                      ? <span style={{
                          display:        'inline-block',
                          fontSize:       'var(--text-small)',
                          padding:        '2px 8px',
                          borderRadius:   'var(--radius-pill)',
                          background:     'var(--color-accent-soft)',
                          color:          'var(--color-accent-dark)',
                          fontWeight:     'var(--font-semibold)'
                        }}>{s.owner_name}</span>
                      : <span style={{ color: 'var(--color-text-faint)' }}>unassigned</span>
                    }
                  </td>
                  <td>{s.fuel_brand || '—'}</td>
                  <td>{s.pos_type || '—'}</td>
                  <td className="cell-mono">{formatDate(s.install_date) || '—'}</td>
                  <td className="cell-mono">{formatDate(s.renewal_date) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
